/* RESPONSIBLE TEAM: team-proactive-support */
import Service from '@ember/service';
import { get } from 'embercom/lib/ajax';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { COPILOT_SEAT_TYPE } from 'embercom/lib/settings/seats/constants';
import {
  FIN_AI_COPILOT_BASE_ID,
  FIN_FOR_PLATFORMS_BASE_ID,
  INCLUDED_TIERS_FROM_PRICING_ENDPOINT,
} from 'embercom/lib/billing';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import { ChargeModel } from 'embercom/models/billing/price/charge';

const MAX_ENDING_UNIT = 9999999999999;

export type TierPriceFormat = ChargeModel.FlatFee | ChargeModel.PerUnit;

export default class CopilotDataService extends Service {
  @service appService: any;
  @service customerService: any;

  @tracked contractedLimit = 0;
  @tracked pending = 0;
  @tracked seatPrice = 0;
  @tracked seatUsage = 0;
  @tracked copilotPrice = 0;
  @tracked tiers: {
    priceFormat: TierPriceFormat;
    unitAmount: number;
    flatAmount: number;
    startingUnit: number;
    endingUnit: number;
  }[] = [];

  get copilotData() {
    return {
      seatUsages: {
        contractedLimit: this.contractedLimit,
        pending: this.pending,
        used: this.seatUsage,
        price: this.seatPrice,
      },
      price: this.copilotPrice,
      tiers: this.tiers,
    };
  }

  async ensureSetup() {
    await this.fetchSeatUsage();
    let price = await this.loadPrice();
    this.setCopilotPrice(price);
    this.setTiers(price);

    return this.copilotData;
  }

  async loadPrice() {
    try {
      await this.customerService.bulkLoadPrices([
        {
          planIds: [Number(this.planId)],
          includeTiers: INCLUDED_TIERS_FROM_PRICING_ENDPOINT,
        },
      ]);

      return this.customerService.prices.find((price: any) =>
        price.hasSamePlans([Number(this.planId)]),
      );
    } catch (e) {
      console.error('Failed to load Copilot price', e);
    }
  }

  updateSeatUsage(change: 'increment' | 'decrement') {
    if (change === 'increment') {
      this.seatUsage++;
    } else {
      this.seatUsage--;
    }
  }

  async fetchSeatUsage() {
    await this.customerService.ensureDataIsLoaded.perform({ fetchPrices: false });
    let seatsUsed = await get('/ember/settings/seats/used_and_pending_seat_count', {
      app_id: this.appService.app.id,
    });

    let copilotSeatUsage = seatsUsed[COPILOT_SEAT_TYPE];

    let { includedSeatCountPerType, pricePerSeatTypes } = this.customerService.customer;

    this.contractedLimit = includedSeatCountPerType[COPILOT_SEAT_TYPE] || 0;
    this.seatUsage = copilotSeatUsage?.used || 0;
    this.pending = copilotSeatUsage?.pending || 0;
    this.seatPrice = pricePerSeatTypes[COPILOT_SEAT_TYPE] || 0;
  }

  private get planId() {
    return this.customerService.activePlanIds.includes(Number(FIN_FOR_PLATFORMS_BASE_ID))
      ? FIN_FOR_PLATFORMS_BASE_ID
      : FIN_AI_COPILOT_BASE_ID;
  }

  private setCopilotPrice(price: any) {
    if (this.planId === FIN_FOR_PLATFORMS_BASE_ID) {
      this.copilotPrice = price?.getChargeForMetric(Metric.copilot_seat_count)?.per_unit_price ?? 0;
      return;
    }
    if (price?.tiers) {
      this.copilotPrice = price.currentTier.unit_amount || price.currentTier.flat_amount || 0;
      return;
    }
    this.copilotPrice = price?.perUnitPrice ?? 0;
  }

  private setTiers(price: any) {
    if (!price?.tiers) {
      this.tiers = [];
      return;
    }

    this.tiers = Array.from(price.tiers).map((tier: any) => {
      return {
        priceFormat:
          tier.price_format === String(ChargeModel.FlatFee)
            ? ChargeModel.FlatFee
            : ChargeModel.PerUnit,
        unitAmount: Number(tier.unit_amount),
        flatAmount: Number(tier.flat_amount),
        startingUnit: Number(tier.starting_unit),
        endingUnit: Number(tier.ending_unit === MAX_ENDING_UNIT ? Infinity : tier.ending_unit),
      };
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    copilotDataService: CopilotDataService;
    'copilot-data-service': CopilotDataService;
  }
}
